import React, { useEffect, useState, useCallback, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { Store, Users, ShoppingBag, MessageCircle, RefreshCw } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Skeleton from '../../components/common/Skeleton';
import Icon from '../../components/common/Icon';
import EmptyState from '../../components/common/EmptyState';
import Select from '../../components/common/Select';
import { adminService } from '../../services/adminService';
import { formatPrice } from '../../utils/priceCalculator';

const StoresByTypeChart = lazy(() =>
  import('./AdminCharts').then((m) => ({ default: m.StoresByTypeChart }))
);
const OrdersByStatusChart = lazy(() =>
  import('./AdminCharts').then((m) => ({ default: m.OrdersByStatusChart }))
);

const KPI = ({ icon, label, value, hint }) => (
  <Card className="p-5">
    <div className="flex items-start justify-between gap-2 mb-2">
      <span className="text-sm text-app-text-secondary">{label}</span>
      <span className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
        <Icon as={icon} size="md" />
      </span>
    </div>
    <div className="text-3xl font-extrabold text-app-text">{value}</div>
    {hint && <div className="text-xs text-app-text-secondary mt-1">{hint}</div>}
  </Card>
);

const ChartSkeleton = () => <Skeleton variant="block" height={240} />;

// Calcula intervalo de datas a partir de um preset.
function rangeFromPreset(preset) {
  const today = new Date();
  const to = today.toISOString().slice(0, 10);
  const fromDate = new Date(today);
  switch (preset) {
    case '7d':
      fromDate.setDate(fromDate.getDate() - 7);
      break;
    case '30d':
      fromDate.setDate(fromDate.getDate() - 30);
      break;
    case '90d':
      fromDate.setDate(fromDate.getDate() - 90);
      break;
    case 'ytd':
      fromDate.setMonth(0, 1);
      break;
    case 'all':
    default:
      return { from: '', to: '' };
  }
  return { from: fromDate.toISOString().slice(0, 10), to };
}

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [stores, setStores] = useState([]);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [storeId, setStoreId] = useState('');

  // Carrega lojas pra popular o seletor.
  useEffect(() => {
    adminService
      .listStores({ limit: 100, includeInactive: true })
      .then((data) => setStores(data?.rows || []))
      .catch(() => setStores([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { from, to } = rangeFromPreset(period);
      const data = await adminService.getStats({
        from: from || undefined,
        to: to || undefined,
        storeId: storeId || undefined
      });
      setStats(data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [period, storeId]);

  useEffect(() => {
    load();
  }, [load]);

  const periodOptions = [
    { value: '7d', label: 'Últimos 7 dias' },
    { value: '30d', label: 'Últimos 30 dias' },
    { value: '90d', label: 'Últimos 90 dias' },
    { value: 'ytd', label: 'Este ano' },
    { value: 'all', label: 'Tudo' }
  ];

  const storeOptions = [
    { value: '', label: 'Todas as lojas' },
    ...stores.map((s) => ({ value: String(s.id), label: `${s.name} (${s.slug})` }))
  ];

  return (
    <div>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-app-text">Admin Reviter</h1>
          <p className="text-sm text-app-text-secondary">
            {storeId
              ? `Visão da loja ${stores.find((s) => String(s.id) === storeId)?.name || ''}.`
              : 'Visão global da plataforma.'}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin-reviter/lojas">
            <Button variant="outline" size="sm">
              Ver lojas
            </Button>
          </Link>
          <Link to="/admin-reviter/usuarios">
            <Button variant="outline" size="sm">
              Ver usuários
            </Button>
          </Link>
        </div>
      </div>

      <Card className="p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-xs text-app-text-secondary mb-1 block">Período</label>
            <Select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              options={periodOptions}
            />
          </div>
          <div>
            <label className="text-xs text-app-text-secondary mb-1 block">Loja</label>
            <Select
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              options={storeOptions}
            />
          </div>
          <div>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<RefreshCw size={14} />}
              onClick={load}
              disabled={loading}
            >
              Atualizar
            </Button>
          </div>
        </div>
      </Card>

      {loading && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="block" height={100} />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Skeleton variant="block" height={280} />
            <Skeleton variant="block" height={280} />
          </div>
        </div>
      )}

      {!loading && err && (
        <div className="max-w-3xl mx-auto">
          <EmptyState title="Erro ao carregar dashboard" description={err} />
        </div>
      )}

      {!loading && !err && stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KPI
              icon={Store}
              label="Lojas"
              value={stats.stores.total}
              hint={`${stats.stores.active} ativas`}
            />
            <KPI
              icon={Users}
              label="Usuários"
              value={stats.users.total}
              hint={`${stats.users.active} ativos`}
            />
            <KPI
              icon={ShoppingBag}
              label="Pedidos"
              value={stats.orders.total}
              hint={`${stats.orders.byStatus?.paid || 0} pagos`}
            />
            <KPI
              icon={MessageCircle}
              label="Leads"
              value={stats.leads.total}
              hint="Total catálogo"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Card className="p-5">
              <h2 className="text-lg font-bold text-app-text mb-4">Lojas por modelo</h2>
              <Suspense fallback={<ChartSkeleton />}>
                <StoresByTypeChart data={stats.stores.byBusinessType} />
              </Suspense>
            </Card>
            <Card className="p-5">
              <h2 className="text-lg font-bold text-app-text mb-4">Pedidos por status</h2>
              <Suspense fallback={<ChartSkeleton />}>
                <OrdersByStatusChart data={stats.orders.byStatus} />
              </Suspense>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-5">
              <p className="text-sm text-app-text-secondary">Recebido</p>
              <p className="text-2xl font-extrabold text-success mt-1">
                {formatPrice(stats.payments.totalPaidAmount)}
              </p>
              <p className="text-xs text-app-text-secondary mt-1">
                {stats.payments.total} pagamentos no período
              </p>
            </Card>
            <Card className="p-5">
              <p className="text-sm text-app-text-secondary">A receber</p>
              <p className="text-2xl font-extrabold text-warning mt-1">
                {formatPrice(stats.payments.totalPendingAmount)}
              </p>
            </Card>
            <Card className="p-5">
              <p className="text-sm text-app-text-secondary">Vencido</p>
              <p className="text-2xl font-extrabold text-error mt-1">
                {formatPrice(stats.payments.totalOverdueAmount)}
              </p>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
